import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import { Search, Users, Calendar, Clock, CheckCircle, Circle, Dumbbell, Swords, ClipboardList } from 'lucide-react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useEndpoints } from '../../hooks/useEndpoints';
import { EVENT_TYPES } from '../../enums/event';
import styles from './TeamActivitiesTab.module.scss';
import EventDetailDrawer from './EventDetailDrawer/EventDetailDrawer';
import CustomTable from '../../shared/CustomTable/CustomTable';

dayjs.extend(isoWeek);

interface TeamActivitiesTabProps {
  teamId: string;
}

type FilterKey = 'all' | 'workout' | 'match' | 'test';

const FILTER_LABELS: Record<FilterKey, string> = {
  all: 'Tutti',
  workout: 'Allenamenti',
  match: 'Gare',
  test: 'Test',
};

const FILTER_TYPES: Record<FilterKey, string[]> = {
  all: [],
  workout: [EVENT_TYPES.WORKOUT],
  match: [EVENT_TYPES.MATCH],
  test: [EVENT_TYPES.TEST, EVENT_TYPES.SYSTEM_TEST_EVENT],
};

const EVENT_TYPE_LABEL: Record<string, string> = {
  [EVENT_TYPES.WORKOUT]: 'Allenamento',
  [EVENT_TYPES.MATCH]: 'Gara',
  [EVENT_TYPES.TEST]: 'Test',
  [EVENT_TYPES.SYSTEM_TEST_EVENT]: 'Test',
};

const EVENT_BADGE_CLASS: Record<string, string> = {
  [EVENT_TYPES.WORKOUT]: 'badgeWorkout',
  [EVENT_TYPES.MATCH]: 'badgeMatch',
  [EVENT_TYPES.TEST]: 'badgeTest',
  [EVENT_TYPES.SYSTEM_TEST_EVENT]: 'badgeTest',
};

const EVENT_ICON: Record<string, React.ReactNode> = {
  [EVENT_TYPES.WORKOUT]: <Dumbbell size={12} />,
  [EVENT_TYPES.MATCH]: <Swords size={12} />,
  [EVENT_TYPES.TEST]: <ClipboardList size={12} />,
  [EVENT_TYPES.SYSTEM_TEST_EVENT]: <ClipboardList size={12} />,
};

function getEventName(event: any): string {
  return (
    event.name ??
    event.testType ??
    (typeof event.testId === 'object' ? event.testId?.name : null) ??
    '—'
  );
}

function getCompiledStatus(event: any): { label: string; className: string } {
  const total = Array.isArray(event.userIds) ? event.userIds.length : (Array.isArray(event.users) ? event.users.length : 0);
  const compiled = Array.isArray(event.compiledIds) ? event.compiledIds.length : 0;
  if (total === 0) return { label: '—', className: styles.compiledNone };
  if (compiled >= total) return { label: 'Completato', className: styles.compiledDone };
  if (compiled > 0) return { label: `${compiled}/${total}`, className: styles.compiledPartial };
  return { label: 'Non compilato', className: styles.compiledNone };
}

function formatDuration(event: any): string | null {
  if (!event.startDate || !event.endDate) return null;
  const diff = dayjs(event.endDate).diff(dayjs(event.startDate), 'minute');
  if (diff < 60) return `${diff} min`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function TeamActivitiesTab({ teamId }: TeamActivitiesTabProps) {
  const endpoints = useEndpoints();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);

  const query = useQuery({
    queryKey: ['team-events', teamId],
    queryFn: async () => {
      const { data } = await endpoints.getEvents({ teams: [teamId], sort: '-startDate', limit: 200 });
      const docs = data?.docs ?? data ?? [];
      return Array.isArray(docs) ? docs : [];
    },
    enabled: !!teamId,
  });

  const events = query.data ?? [];

  const filtered = useMemo(() => {
    let list = events;

    // Filter by type
    const types = FILTER_TYPES[filter];
    if (types.length > 0) {
      list = list.filter((e: any) => types.includes(e.type));
    }

    // Filter by search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e: any) => {
        const name = getEventName(e).toLowerCase();
        const location = (e.location ?? '').toLowerCase();
        return name.includes(q) || location.includes(q);
      });
    }

    return list;
  }, [events, filter, search]);

  // Stats
  const stats = useMemo(() => {
    const now = dayjs();
    const weekStart = now.startOf('isoWeek');
    const thisWeek = events.filter((e: any) => {
      const d = dayjs(e.startDate ?? e.date);
      return d.isAfter(weekStart) && d.isBefore(now.add(1, 'day'));
    });
    const workouts = events.filter((e: any) => e.type === EVENT_TYPES.WORKOUT).length;
    const matches = events.filter((e: any) => e.type === EVENT_TYPES.MATCH).length;
    const tests = events.filter((e: any) =>
      e.type === EVENT_TYPES.TEST || e.type === EVENT_TYPES.SYSTEM_TEST_EVENT
    ).length;

    return { total: events.length, thisWeek: thisWeek.length, workouts, matches, tests };
  }, [events]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const openEvent = (event: any) => setSelectedEvent({ ...event, date: event.date ?? event.startDate });

  const clickableCell = (content: React.ReactNode, record: any) => (
    <div style={{ cursor: 'pointer' }} onClick={() => openEvent(record)}>
      {content}
    </div>
  );

  const tableHeaders = ['Data', 'Tipo', 'Nome', 'Luogo', 'Durata', 'Atleti', 'Stato'];

  const tableFields = {
    Data: {
      key: 'startDate',
      customComponent: (_: any, event: any) => {
        const dateValue = event.startDate ?? event.date;
        return clickableCell(
          <span className={styles.tdDate}>{dateValue ? dayjs(dateValue).format('DD MMM YYYY') : '—'}</span>,
          event,
        );
      },
    },
    Tipo: {
      key: 'type',
      customComponent: (_: any, event: any) =>
        clickableCell(
          <span className={`${styles.typeBadge} ${styles[EVENT_BADGE_CLASS[event.type]] ?? ''}`}>
            {EVENT_ICON[event.type]}
            {EVENT_TYPE_LABEL[event.type] ?? event.type}
          </span>,
          event,
        ),
    },
    Nome: {
      key: 'name',
      customComponent: (_: any, event: any) => clickableCell(<span className={styles.tdName}>{getEventName(event)}</span>, event),
    },
    Luogo: {
      key: 'location',
      customComponent: (_: any, event: any) => clickableCell(<span className={styles.tdMuted}>{event.location || '—'}</span>, event),
    },
    Durata: {
      key: 'startDate',
      customComponent: (_: any, event: any) => {
        const duration = formatDuration(event);
        return clickableCell(
          duration ? (
            <span className={styles.duration}>
              <Clock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {duration}
            </span>
          ) : (
            <>—</>
          ),
          event,
        );
      },
    },
    Atleti: {
      key: 'userIds',
      align: 'end' as const,
      customComponent: (_: any, event: any) => {
        const athleteCount = Array.isArray(event.userIds)
          ? event.userIds.length
          : (Array.isArray(event.users) ? event.users.length : 0);
        return clickableCell(
          <span className={styles.athleteCount}>
            <Users className={styles.athleteIcon} />
            {athleteCount}
          </span>,
          event,
        );
      },
    },
    Stato: {
      key: '_id',
      align: 'end' as const,
      customComponent: (_: any, event: any) => {
        const compiled = getCompiledStatus(event);
        return clickableCell(
          <span className={`${styles.compiledBadge} ${compiled.className}`}>
            {compiled.className === styles.compiledDone ? <CheckCircle size={12} /> : <Circle size={12} />}
            {compiled.label}
          </span>,
          event,
        );
      },
    },
  };

  if (query.isPending) {
    return (
      <div className={styles.loadingState}>
        <LeopartLoader />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={`${styles.statCard} ${styles.statCardTotal}`}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIconWrap} ${styles.statIconTotal}`}>
              <Calendar size={16} />
            </div>
            <p className={styles.statLabel}>Totale attività</p>
          </div>
          <p className={styles.statValue}>{stats.total}</p>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIconWrap} ${styles.statIconWeek}`}>
              <Clock size={16} />
            </div>
            <p className={styles.statLabel}>Questa settimana</p>
          </div>
          <p className={styles.statValue}>{stats.thisWeek}</p>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIconWrap} ${styles.statIconWorkout}`}>
              <Dumbbell size={16} />
            </div>
            <p className={styles.statLabel}>Allenamenti</p>
          </div>
          <p className={styles.statValue}>{stats.workouts}</p>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIconWrap} ${styles.statIconMatch}`}>
              <Swords size={16} />
            </div>
            <p className={styles.statLabel}>Gare</p>
          </div>
          <p className={styles.statValue}>{stats.matches}</p>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIconWrap} ${styles.statIconTest}`}>
              <ClipboardList size={16} />
            </div>
            <p className={styles.statLabel}>Test</p>
          </div>
          <p className={styles.statValue}>{stats.tests}</p>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filterBar}>
        <div className={styles.filterLeft}>
          <div className={styles.filterPills}>
            {(Object.keys(FILTER_LABELS) as FilterKey[]).map(k => (
              <button
                key={k}
                className={`${styles.filterPill} ${filter === k ? styles.filterPillActive : ''}`}
                onClick={() => { setFilter(k); setPage(1); }}
              >
                {FILTER_LABELS[k]}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.searchBox}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Cerca attività..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <Calendar size={40} strokeWidth={1.2} style={{ color: 'var(--c-text-3)' }} />
          <p className={styles.emptyTitle}>Nessuna attività trovata</p>
          <p className={styles.emptySubtitle}>
            {search || filter !== 'all'
              ? 'Prova a cambiare i filtri di ricerca'
              : 'Le attività appariranno quando verranno create eventi per questo team'}
          </p>
        </div>
      ) : (
        <div className={styles.tableCard}>
          <CustomTable headers={tableHeaders} data={paged} fields={tableFields} />

          {totalPages > 1 && (
            <div className={styles.paginationRow}>
              <button
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Precedente
              </button>
              <span className={styles.pageInfo}>{page} / {totalPages}</span>
              <button
                className={styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Successiva
              </button>
            </div>
          )}
        </div>
      )}

      <EventDetailDrawer
        event={selectedEvent}
        teamId={teamId}
        onClose={() => setSelectedEvent(null)}
      />
    </div>
  );
}
